<template>
  <DescriptionEditor
    :editor-id="field.type"
    :value="field.html"
    :highlights="highlights"
    :nudges="nudges"
    :is-read-only="isReadOnly"
    :is-locked="isLocked"
    :is-template="isTemplate"
    :allow-lock-toggle="allowLockToggle"
    :show-lock-icon="showLockIcon"
    :disabled-nodes="field.disabledNodes"
    :placeholder="placeholder"
    :ai-suggestions-loading="aiSuggestionsLoading"
    :ai-suggestions="aiSuggestions"
    :focused-editor-id="focusedEditorId"
    @update:model-value="$emit('input-editor', { ...$event, isLocked: field.isLocked })"
    @create="$emit('create', $event)"
    @copy-content="$emit('copyContent', $event)"
    @paste-content="$emit('pasteContent', $event)"
    @toggle-lock="onLockToggle(fieldIndex)"
    @track-view-highlight="$emit('trackViewHighlight', $event)"
    @track-apply-highlight="$emit('trackApplyHighlight', $event)"
    @track-view-nudge="$emit('trackViewNudge', $event)"
    @track-apply-nudge="$emit('trackApplyNudge', $event)"
    @link-edit="onLinkEdit"
    @show-apply-template="onShowApplyTemplate"
    @ai-suggest-click="onAiSuggestClick"
    @add-editor="onAddEditor"
    @blur-editor="onBlurEditor"
    @remove-editor="onRemoveEditor"
    @update-selected-json="onUpdateSelectedJson"
    @focus-editor="onFocusEditor"
  />
</template>

<script>
import { mapActions, mapState, storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useApplyTemplate } from '@/stores/document/applyTemplate';
import { useDocumentAiSuggestion } from '@/stores/document/aiSuggestion';
import { useDocumentEditor } from '@/stores/document/editor';
import { useDocumentEditorToolbar } from '@/stores/document/editorToolbar';
import { useHighlights } from '@/stores/highlights/highlights';
import { useNudges } from '@/stores/highlights/nudges';
import { useEditor } from '@/stores/editor/editor';
import DescriptionEditor from '@ui/components/editor/DescriptionEditor/DescriptionEditor.vue';

export default {
  name: 'DescriptionEditorContainer',
  components: { DescriptionEditor },
  props: {
    fieldIndex: { type: Number, default: 0 },
    isEditable: { type: Boolean, default: true },
    isTemplate: { type: Boolean, default: false },
  },
  emits: [
    'input-editor',
    'create',
    'copyContent',
    'pasteContent',
    'trackViewHighlight',
    'trackApplyHighlight',
    'trackViewNudge',
    'trackApplyNudge',
    'lockChange',
  ],

  computed: {
    ...mapState(useAppAuthData, ['authData']),
    ...mapState(useEditor, ['isPublishedTemplate']),
    ...mapState(useDocumentEditor, ['fields']),
    ...mapState(useHighlights, ['descriptionHighlights']),
    ...mapState(useNudges, ['fieldNudges']),

    field() {
      return this.fields[this.fieldIndex] || {};
    },

    highlights() {
      return this.descriptionHighlights.filter((hl) => hl.fieldId === this.field.id);
    },

    nudges() {
      return this.fieldNudges[this.field.type];
    },

    showLockIcon() {
      if (!this.isEditable) {
        return true;
      }

      if (this.field.isLocked || this.field.isHeaderOrFooter) {
        return true;
      }

      return this.isTemplate && this.field.isLockable;
    },

    isLocked() {
      // the document is not editable at all
      if (!this.isEditable) {
        return true;
      }

      if (this.isPublishedTemplate) {
        return true;
      }

      // the field is special, we show it as always locked
      if (this.field.isHeaderOrFooter) {
        return true;
      }

      return this.field.isLocked;
    },

    isReadOnly() {
      // the document is not editable at all
      if (!this.isEditable) {
        return true;
      }

      if (this.isPublishedTemplate) {
        return true;
      }

      // the field is special, it's always locked, but its content can be edited in template
      if (this.field.isHeaderOrFooter) {
        return !this.isTemplate;
      }

      return this.field.isLocked;
    },

    allowLockToggle() {
      return this.isTemplate && this.field.isLockable && !this.field.isHeaderOrFooter;
    },

    placeholder() {
      if (this.field.isHeaderOrFooter) {
        return 'Any text in this section will be locked for jobs created using this template';
      }
      return undefined;
    },

    aiSuggestionsLoading() {
      return storeToRefs(useDocumentAiSuggestion()).isLoading;
    },

    aiSuggestions() {
      return storeToRefs(useDocumentAiSuggestion()).aiSuggestions;
    },

    focusedEditorId() {
      return useDocumentEditor().focusId;
    },
  },

  methods: {
    ...mapActions(useDocumentEditor, ['toggleLock']),

    onLockToggle(fieldIndex) {
      if (this.allowLockToggle) {
        this.$emit('lockChange', { fieldIndex, isLocked: !this.field.isLocked });
        this.toggleLock(fieldIndex);
      }
    },

    onAddEditor({ editorId, tiptap }) {
      useDocumentEditor().addTiptap(editorId, tiptap);
    },

    onBlurEditor() {
      useDocumentEditor().updateFocus('');
    },

    onRemoveEditor({ editorId }) {
      useDocumentEditor().removeTiptap(editorId);
    },

    onUpdateSelectedJson({ editorId, extensions }) {
      useDocumentEditor().updateSelectedJson({ editorId, extensions });
    },

    onFocusEditor({ editorId }) {
      useDocumentEditor().updateFocus(editorId);
    },

    onLinkEdit() {
      useDocumentEditorToolbar().onLinkButtonClick();
    },

    onShowApplyTemplate() {
      useApplyTemplate().setModalVisible(true);
    },

    async onAiSuggestClick({
      id, ruleId, text, className,
    }) {
      await useDocumentAiSuggestion().generateTooltipSuggestions({
        id,
        ruleId,
        text,
        className,
      });
    },
  },
};
</script>
